import axiosNest from '../api/axiosNest';

/**
 * Pliki od klienta przy prezleceniu (decyzja użytkownika 26.10.2026: z P5
 * teraz tylko wgrywanie pliku do ręcznego prezlecenia; przełącznik
 * `preOrderAttachments`, S-26.10-13, paczka CS i Administracja).
 * Odpowiada `pre-order/pre-order-attachments.ts` w API.
 */
export const FEATURE_PRE_ORDER_ATTACHMENTS = 'preOrderAttachments';

/** Lustro `MIN_DELETE_REASON_LENGTH` z API. */
export const MIN_DELETE_REASON_LENGTH = 10;

export interface PreOrderAttachment {
  id: number;
  filename: string;
  kind: string;
  kindLabel: string;
  contentType: string;
  size: number;
  sizeLabel: string;
  inline: boolean;
  checksum: string;
  uploadedBy: string | null;
  createdAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
  deletedReason: string | null;
}

export interface PreOrderAttachmentList {
  preOrderId: number;
  attachments: PreOrderAttachment[];
  uploadRefusal: string | null;
  deleteRefusal: string | null;
  storageAvailable: boolean;
  limits: { maxBytes: number; maxCount: number; extensions: string[] };
}

const base = (preOrderId: number | string) => `/pre-orders/${preOrderId}/attachments`;

/** Bez limitów w odpowiedzi — dołączanie zamknięte (lista rozszerzeń pusta). */
const NO_LIMITS: PreOrderAttachmentList['limits'] = { maxBytes: 0, maxCount: 0, extensions: [] };

/**
 * Lista plików. Odpowiedź w innym kształcie (np. starsze API) nie wywraca
 * karty — pusta lista bez dołączania.
 */
export async function fetchAttachments(preOrderId: number | string): Promise<PreOrderAttachmentList> {
  const { data } = await axiosNest.get<Partial<PreOrderAttachmentList> | null>(base(preOrderId));
  const ok = Array.isArray(data?.attachments) && !!data?.limits && Array.isArray(data.limits.extensions);
  return {
    preOrderId: Number(preOrderId),
    attachments: ok ? (data!.attachments as PreOrderAttachment[]) : [],
    uploadRefusal: ok ? (data!.uploadRefusal ?? null) : 'Nie udało się odczytać listy plików — odśwież kartę',
    deleteRefusal: data?.deleteRefusal ?? null,
    storageAvailable: ok ? data!.storageAvailable !== false : true,
    limits: ok ? data!.limits! : NO_LIMITS,
  };
}

export async function uploadAttachment(preOrderId: number | string, file: File): Promise<PreOrderAttachment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axiosNest.post<PreOrderAttachment>(base(preOrderId), form);
  return data;
}

export async function deleteAttachment(
  preOrderId: number | string,
  attachmentId: number,
  reason: string,
): Promise<PreOrderAttachment> {
  const { data } = await axiosNest.delete<PreOrderAttachment>(`${base(preOrderId)}/${attachmentId}`, {
    data: { reason },
  });
  return data;
}

/** Plik jako blob (z tokenem) — do podglądu na miejscu albo zapisania na dysk. */
export async function fetchAttachmentBlob(preOrderId: number | string, attachmentId: number): Promise<Blob> {
  const { data } = await axiosNest.get<Blob>(`${base(preOrderId)}/${attachmentId}/file`, {
    params: { download: 1 },
    responseType: 'blob',
  });
  return data;
}

/**
 * Odmowa przyjęcia pliku przez serwer pośredniczący (413 bez treści z API) —
 * zdanie dla operatora zamiast „Request failed with status code 413”.
 */
export const TOO_LARGE_FOR_SERVER =
  'Serwer nie przyjął pliku, bo jest za duży dla jego ustawień — zgłoś to administratorowi; plik nie został dołączony';

export function uploadErrorMessage(err: unknown, fallback: (e: unknown) => string): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 413 ? TOO_LARGE_FOR_SERVER : fallback(err);
}

/** Rozszerzenie małymi literami — jak w API. */
export function extensionOf(name: string): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return m ? m[1].toLowerCase() : '';
}

/**
 * Sprawdzenie przed wysłaniem (rozmiar, rodzaj) — żeby nie wysyłać 20 MB po
 * odmowę. Ostateczne słowo ma API (sprawdza też początek pliku).
 */
export function localRefusal(
  file: { name: string; size: number },
  limits: PreOrderAttachmentList['limits'],
): string | null {
  if (file.size === 0) return 'Plik jest pusty';
  if (file.size > limits.maxBytes) {
    return `Plik jest za duży — najwyżej ${Math.round(limits.maxBytes / (1024 * 1024))} MB`;
  }
  const ext = extensionOf(file.name);
  if (!limits.extensions.includes(ext)) {
    return `Tego rodzaju pliku nie przyjmujemy${ext ? ` (.${ext})` : ''} — dozwolone: PDF, arkusze, dokumenty, obrazy i zapisane wiadomości`;
  }
  return null;
}
